import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Box, Alert, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useMutation } from 'react-query';
import apiClient from '../../request/http-common';

export default function UploadWallet() {
  const [walletName, setWalletName] = useState('');
  const [walletImage, setWalletImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { isLoading: isUploading, mutate: uploadWallet } = useMutation(
    async () => {
      const formData = new FormData();
      formData.append('name', walletName);
      formData.append('image', walletImage);

      return await apiClient.post('/api/upload-wallet', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setWalletName('');
        setWalletImage(null);
        setImagePreview(null);
        
        const event = new Event('processed');
        window.dispatchEvent(event);

        setTimeout(() => {
          setSuccess('');
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.message || 'Failed to upload wallet';
        setError(errorMessage);
        setSuccess('');
        
        const event = new Event('processed');
        window.dispatchEvent(event);
      },
    }
  );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, JPG, GIF, SVG, WEBP)');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2048000) {
        setError('Image size must be less than 2MB');
        return;
      }

      setWalletImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!walletName.trim()) {
      setError('Wallet name is required');
      return;
    }

    if (!walletImage) {
      setError('Wallet image is required');
      return;
    }

    const event = new Event('processing');
    window.dispatchEvent(event);
    uploadWallet();
  };

  return (
    <div style={{ marginTop: '75px' }}>
      <Container maxWidth="md">
        <Typography
          mt={4}
          mb={3}
          sx={{ fontWeight: 'bold', fontSize: 24, textAlign: 'center', textTransform: 'uppercase' }}
          variant="h5"
        >
          Upload Crypto Wallet
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Wallet Name"
            value={walletName}
            onChange={(e) => setWalletName(e.target.value)}
            margin="normal"
            required
            placeholder="e.g., MetaMask, Trust Wallet"
          />

          <Box sx={{ mt: 3, mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{ py: 2 }}
            >
              {walletImage ? 'Change Image' : 'Upload Image'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
          </Box>

          {imagePreview && (
            <Box sx={{ mt: 2, mb: 3, textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Image Preview:
              </Typography>
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  maxWidth: '200px',
                  maxHeight: '200px',
                  borderRadius: '8px',
                  border: '2px solid #ccc',
                }}
              />
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isUploading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {isUploading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              'Upload Wallet'
            )}
          </Button>
        </Box>
      </Container>
    </div>
  );
}


